import { io, Socket } from 'socket.io-client';
import { SocketEvents } from '../types';

class SocketService {
  private socket: Socket | null = null;
  private sessionCode: string = '';

  private getDefaultServerUrl(): string {
    // Priorité 1: Variable d'environnement
    if (process.env.REACT_APP_SERVER_URL) {
      return process.env.REACT_APP_SERVER_URL;
    }

    // Priorité 2: Même host que le frontend avec le port du backend
    const protocol = window.location.protocol;
    const hostname = window.location.hostname;
    const backendPort = process.env.REACT_APP_BACKEND_PORT || '5001';
    
    // En production, utiliser le même port que le frontend
    if (process.env.NODE_ENV === 'production') {
      return `${protocol}//${hostname}`;
    }
    
    // En développement, utiliser le port spécifique du backend
    return `${protocol}//${hostname}:${backendPort}`;
  }

  connect(serverUrl?: string): Socket {
    if (this.socket?.connected) {
      return this.socket;
    }

    // Déterminer l'URL du serveur automatiquement
    const defaultServerUrl = this.getDefaultServerUrl();
    const finalServerUrl = serverUrl || defaultServerUrl;

    this.socket = io(finalServerUrl, {
      transports: ['websocket'],
      autoConnect: true,
    });

    this.socket.on('connect', () => {
      console.log('🔌 Connecté au serveur Socket.IO');
    });

    this.socket.on('disconnect', () => {
      console.log('🔌 Déconnecté du serveur Socket.IO');
    });

    this.socket.on('error', (error) => {
      console.error('❌ Erreur Socket.IO:', error);
    });

    return this.socket;
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  joinSession(sessionCode: string, userType: 'teacher' | 'student', userId: string, userName: string): void {
    if (!this.socket) {
      console.error('Socket non initialisé');
      return;
    }

    this.sessionCode = sessionCode;
    this.socket.emit('join-session', {
      sessionCode,
      userType,
      userId,
      userName,
    });
  }

  leaveSession(): void {
    if (!this.socket || !this.sessionCode) return;

    this.socket.emit('leave-session', {
      sessionCode: this.sessionCode,
      userName: 'Utilisateur',
    });
  }

  startSession(): void {
    if (!this.socket || !this.sessionCode) return;
    this.socket.emit('start-session', { sessionCode: this.sessionCode });
  }

  stopSession(): void {
    if (!this.socket || !this.sessionCode) return;
    this.socket.emit('stop-session', { sessionCode: this.sessionCode });
  }

  activateQuestion(questionId: string): void {
    if (!this.socket || !this.sessionCode) return;
    this.socket.emit('activate-question', {
      questionId,
    });
  }

  submitAnswer(questionId: string, studentId: string, studentName: string, answer: string): void {
    if (!this.socket || !this.sessionCode) return;
    const data = {
      sessionCode: this.sessionCode,
      questionId,
      studentId,
      studentName,
      answer,
    };
    console.log('submitAnswer', data);

    this.socket.emit('submit-answer', data);
  }

  // Event listeners
  on<K extends keyof SocketEvents>(event: K, callback: SocketEvents[K]): void {
    if (!this.socket) return;
    this.socket.on(event, callback as any);
  }

  off<K extends keyof SocketEvents>(event: K, callback?: SocketEvents[K]): void {
    if (!this.socket) return;
    if (callback) {
      this.socket.off(event, callback as any);
    } else {
      this.socket.off(event);
    }
  }

  getSocket(): Socket | null {
    return this.socket;
  }

  isConnected(): boolean {
    return this.socket?.connected || false;
  }
}

export const socketService = new SocketService();
